import { useLayoutStore } from '@src/stores-v2/StoreContext';
import { useCallback, useEffect, useRef } from 'react';

export function useTranscriptLayoutResize() {
  const layoutStore = useLayoutStore();
  const {
    transcriptWidth,
    transcriptMinWidth,
    transcriptMaxWidth,
    isDragging,
  } = layoutStore;

  const initialMouseX = useRef<number | null>(null);
  const initialWidth = useRef<number | null>(null);
  const latestClientX = useRef<number | null>(null);
  const rafId = useRef<number | null>(null);

  const applyMove = useCallback(
    (clientX: number) => {
      if (initialMouseX.current === null || initialWidth.current === null) {
        return;
      }
      // transcriptWidth is in pixels, so no need to convert to percentage
      const deltaX = clientX - initialMouseX.current;
      const updatedWidth = initialWidth.current + deltaX;

      // Clamp width between min and max
      const newWidth = Math.min(
        Math.max(updatedWidth, transcriptMinWidth),
        transcriptMaxWidth,
      );

      layoutStore.setTranscriptWidth(newWidth);
    },
    [layoutStore, transcriptMinWidth, transcriptMaxWidth],
  );

  // Batch moves to one update per animation frame so the drag tracks the
  // pointer smoothly without flooding the renderer.
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      latestClientX.current = e.clientX;
      if (rafId.current === null) {
        rafId.current = requestAnimationFrame(() => {
          rafId.current = null;
          if (latestClientX.current !== null) {
            applyMove(latestClientX.current);
          }
        });
      }
    },
    [applyMove],
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      initialMouseX.current = e.clientX;
      initialWidth.current = parseFloat(transcriptWidth);
      layoutStore.setIsDragging(true);
    },
    [transcriptWidth, layoutStore],
  );

  const handleMouseUp = useCallback(() => {
    // Flush the final pointer position before clearing drag state, otherwise
    // the last frame's movement is lost and the bar snaps to a stale spot.
    if (rafId.current !== null) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
    if (latestClientX.current !== null) {
      applyMove(latestClientX.current);
    }
    initialMouseX.current = null;
    initialWidth.current = null;
    latestClientX.current = null;
    layoutStore.setIsDragging(false);
  }, [applyMove, layoutStore]);

  // Cancel any pending frame if the component unmounts mid-drag.
  useEffect(() => {
    return () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, []);

  return {
    isDragging,
    handleMouseMove,
    handleMouseDown,
    handleMouseUp,
  };
}
